/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import Types = CommandTypes.Common.Windowcontrol;
import { Commands } from 'bridge-commands/commands';
import { Types as CommandTypes } from 'bridge-commands/types';
import { BrowserPlugin } from 'crimson/lib/bridge/browser-plugin/browser-plugin';
import { PluginHandlers, PluginResponse } from 'crimson/lib/bridge/common/plugin.models';
import { FullBridgeCommand } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { BrowserSupportService } from 'browser-support/lib/browser-support.service';
import { exitTizenApplication, isTizenAPIAvailable } from 'utils/lib/platform/tizen';

const Windowcontrol = Commands.Common.Windowcontrol;

@Injectable({
    providedIn: 'root'
})
export class WindowControlPlugin extends BrowserPlugin {
    private logger: Logger;

    constructor(
        loggingService: LoggingService,
        private browserSupportService: BrowserSupportService
    ) {
        super();
        this.logger = loggingService.getLogger('crimson/browser/windowcontrol-plugin');
    }

    public get handlers(): PluginHandlers {
        return {
            module: Windowcontrol.Maximize.module,
            commands: {
                [Windowcontrol.Maximize.method]: this.maximize.bind(this),
                [Windowcontrol.Minimize.method]: this.minimize.bind(this),
                [Windowcontrol.Restore.method]: this.restore.bind(this),
                [Windowcontrol.Resize.method]: this.resize.bind(this),
                [Windowcontrol.Close.method]: this.close.bind(this),
                [Windowcontrol.Focus.method]: this.focus.bind(this),
                [Windowcontrol.RequestUserAttention.method]: this.requestUserAttention.bind(this)
            }
        };
    }

    // Below command is not used in case of browser
    private maximize(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        return this.makeResult('true');
    }

    // Below command is not used in case of browser
    private minimize(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        return this.makeResult('true');
    }

    // Below command is not used in case of browser
    private restore(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        return this.makeResult('true');
    }

    private resize(command: FullBridgeCommand<Types.WindowRectangle>): Observable<PluginResponse> {
        // handle fullscreen
        if (command.payload.x === 0 && command.payload.y === 0 && command.payload.w === 0 && command.payload.h === 0) {
            const fullscreenElem = document.getElementById('fullscreen-container');
            if (fullscreenElem) {
                this.browserSupportService.requestFullscreen(fullscreenElem);
            }
        }
        return this.makeResult('true');
    }

    private close(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        if (isTizenAPIAvailable()) {
            exitTizenApplication();
        }
        return this.makeResult('true');
    }

    // This was added to bring the child (oauth) window into focus due to chrome issue
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1383.
    // In case of browser there is no way to force child window on top of parent window so
    // returning always as true on browser.
    private focus(command: FullBridgeCommand<Types.FocusParameters>): Observable<PluginResponse> {
        return this.makeResult('true');
    }

    // Below command is not used in case of browser
    private requestUserAttention(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        return this.makeResult('true');
    }
}
